import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { BreadcrumbService } from 'app/breadcrumb.service';
import { LanguageService } from 'app/LanguageService/Language.Service';
import { AppServiceManager } from 'common/services/appServiceManager';
import { AuthService } from 'common/services/auth.service';

export type AgendaBooking = {
  _id: string;
  customerId: string;
  customerName: string;
  customerPhone?: string;
  packageId: string;
  packageName?: string;
  status: string;
  attendancePermissions?: {
  pastAttendance: boolean;
  futureAttendance: boolean;
};
};

export type AgendaSession = {
  _id: string;
  trainerId: string;
  trainerName?: string;
  type: string;
  packageId?: string;
  packageName?: string;
  seriesId?: string;
  localDate: string;
  startMinutes: number;
  durationMinutes: number;
  maxMembers: number;
  bookedCount: number;
  status: string;
  bookings: AgendaBooking[];
};

type MeasurePair = { current: string; goal: string };

type AssessmentAnswers = {
  client: {
    fullName: string;
    dateOfBirth: string;
    age: string;
    gender: string;
    phone: string;
    email: string;
    address: string;
    occupation: string;
    emergencyName: string;
    emergencyPhone: string;
  };
  goals: { selected: string[]; other: string; targetDate: string; motivation: string };
  lifestyle: {
    daysPerWeek: string;
    sleepBand: string;
    stressLevel: string;
    workType: string;
    specificDiet: string;
    dietDetail: string;
    smokes: string;
    alcohol: string;
    waterIntake: string;
  };
  medical: {
    conditions: string;
    conditionsDetail: string;
    injuries: string;
    injuriesDetail: string;
    medication: string;
    medicationDetail: string;
    allergies: string;
    allergiesDetail: string;
  };
  training: {
    trainedBefore: string;
    howLong: string;
    types: string;
    typeList: string[];
    level: string;
    challenges: string;
  };
  measurements: {
    weight: MeasurePair;
    height: MeasurePair;
    waist: MeasurePair;
    hips: MeasurePair;
    chest: MeasurePair;
    arms: MeasurePair;
    thighs: MeasurePair;
  };
  posture: {
    issues: string;
    issuesDetail: string;
    limitations: string;
    limitationsDetail: string;
    flags: string[];
  };
  tests: {
    pushUps: string;
    squat: string;
    plank: string;
    runWalk: string;
    flexibility: string;
    grip: string;
    other: string;
  };
  nutrition: {
    mealsPerDay: string;
    tracksMacros: string;
    dietType: string;
    intolerances: string;
    intolerancesDetail: string;
    hydration: string;
  };
  extra: {
    preferences: string;
    preferencesDetail: string;
    environment: string;
    comments: string;
  };
  program: {
    shortTerm: string;
    midTerm: string;
    longTerm: string;
    frequencyDays: string;
    focus: string[];
    preferredTime: string;
  };
  progress: {
    reviewDate: string;
    nextCheckIn: string;
    notes: string;
    clientDate: string;
    trainerDate: string;
  };
};

type AssessmentForm = {
  clientName: string;
  trainerName: string;
  signedOn: string;
  answers: AssessmentAnswers;
};

@Component({
  selector: 'app-session-attendance',
  templateUrl: './session-attendance.component.html',
  styleUrls: ['./session-attendance.component.css'],
})
export class SessionAttendanceComponent implements OnInit {
  branchId = '';
  userId = '';
  /** Trainer role: agenda locked to own sessions; no trainer dropdown. */
  isTrainerRole = false;
  /** Admin / staff: can pick trainer (or all). */
  showTrainerFilter = false;
  selectedLocalDate = '';
  /** Bound to p-calendar — kept in sync with selectedLocalDate. */
  selectedDate: Date = new Date();
  selectedTrainerId: string | null = null;
  /** Type filter radios — same pattern as trainer-schedule. */
  selectedTypeFilter: 'all' | 'one_to_one' | 'class' | 'program' = 'all';
  /** Default: only sessions that have at least one booking. */
  selectedBookingScope: 'booked' | 'all' = 'booked';
  trainerOptions: { label: string; value: string }[] = [];
  rows: AgendaSession[] = [];
  loading = false;
  markingId: string | null = null;

  classDialogVisible = false;
  classSession: AgendaSession | null = null;
  /** bookingId → Present | Absent pending selection in class dialog */
  classMarks: { [bookingId: string]: 'Present' | 'Absent' } = {};
  classMarkBusy = false;
  assessedIds = new Set<string>();
  assessmentVisible = false;
  assessmentReadOnly = false;
  assessmentSaving = false;
  assessmentBooking: AgendaBooking | null = null;
  assessmentSession: AgendaSession | null = null;
  assessmentForm = this.emptyAssessment();

  constructor(
    private languageService: LanguageService,
    private breadcrumbService: BreadcrumbService,
    private appServiceManager: AppServiceManager,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.userId = String(AuthService.getLoggedInUserId() || '');
    this.branchId = String(AuthService.getLoggedInBranch() || '');
    this.applyRoleScope();

    this.selectedLocalDate = this.toLocalDateString(new Date());
    this.selectedDate = new Date();

    this.breadcrumbService.setItems([
      { label: 'Session Attendance', routerLink: '/session-attendance' },
    ]);

    if (this.showTrainerFilter) {
      this.loadTrainers();
    }
    this.loadAgenda();
  }

  /**
   * Admin / staff → trainer dropdown (all or one trainer).
   * Trainer → only own attendance slots; no dropdown.
   */
  private applyRoleScope(): void {
    const role = AuthService.getLoggedInRole();
    const user = AuthService.getLoggedInUser();
    const roleName = String(role?.name || user?.role || '')
      .trim()
      .toLowerCase();

    this.isTrainerRole = roleName === 'trainer';

    if (this.isTrainerRole) {
      this.showTrainerFilter = false;
      this.selectedTrainerId = this.userId;
      return;
    }

    // Admin, Super Admin, and other privileged non-trainer roles
    this.showTrainerFilter = true;
    this.selectedTrainerId = null;
  }

  get filteredRows(): AgendaSession[] {
    let list = this.rows;
    if (this.selectedBookingScope === 'booked') {
      list = list.filter((r) => (r.bookings || []).length > 0);
    }
    if (this.selectedTypeFilter !== 'all') {
      list = list.filter(
        (r) => this.sessionTone(r) === this.selectedTypeFilter,
      );
    }
    return list;
  }

  setLabel(label: string): string {
    return this.languageService.GetCurrentLanguage(
      'SessionAttendanceComponent',
      label,
    );
  }

  goToday(): void {
    this.selectedDate = new Date();
    this.selectedLocalDate = this.toLocalDateString(this.selectedDate);
    this.loadAgenda();
  }

  goPrevDay(): void {
    this.selectedLocalDate = this.shiftLocalDate(this.selectedLocalDate, -1);
    this.syncSelectedDateFromLocal();
    this.loadAgenda();
  }

  goNextDay(): void {
    this.selectedLocalDate = this.shiftLocalDate(this.selectedLocalDate, 1);
    this.syncSelectedDateFromLocal();
    this.loadAgenda();
  }

  onCalendarSelect(): void {
    if (!this.selectedDate) {
      return;
    }
    this.selectedLocalDate = this.toLocalDateString(this.selectedDate);
    this.loadAgenda();
  }

  private syncSelectedDateFromLocal(): void {
    const [y, m, d] = this.selectedLocalDate.split('-').map(Number);
    if (!y || !m || !d) {
      return;
    }
    this.selectedDate = new Date(y, m - 1, d);
  }

  refresh(): void {
    this.loadAgenda();
  }

  onTrainerChange(): void {
    this.loadAgenda();
  }

  onTrainerSelect(value: string): void {
    this.selectedTrainerId = value ? value : null;
    this.onTrainerChange();
  }

  sessionTitle(s: AgendaSession): string {
    if (s.type === 'class') {
      return s.packageName || this.setLabel('typeClassLabel');
    }
    const b = this.oneToOneBooking(s);
    if (!b) {
      return this.setLabel('typeOneToOneLabel');
    }
    const name = (b.customerName || '').trim();
    return name || this.setLabel('memberFallbackLabel');
  }

  trainerName(s: AgendaSession): string {
    const fromApi = (s.trainerName || '').trim();
    if (fromApi) {
      return fromApi;
    }
    const opt = this.trainerOptions.find((t) => t.value === s.trainerId);
    return (opt?.label || '').trim() || this.setLabel('trainerFallbackLabel');
  }

  packageName(s: AgendaSession): string {
    if (s.type === 'class') {
      return (s.packageName || '').trim() || '—';
    }
    const b = this.oneToOneBooking(s);
    const fromBooking = (b?.packageName || '').trim();
    if (fromBooking) {
      return fromBooking;
    }
    const fromSession = (s.packageName || '').trim();
    if (fromSession) {
      return fromSession;
    }
    // 1:1 invent has no package until booked
    return this.setLabel('openOneToOnePackageLabel');
  }

  sessionTone(
    s?: AgendaSession | null,
  ): 'one_to_one' | 'class' | 'program' {
    if (!s) {
      return 'class';
    }
    if (s.seriesId) {
      return 'program';
    }
    return s.type === 'one_to_one' ? 'one_to_one' : 'class';
  }

  sessionToneLabel(s?: AgendaSession | null): string {
    const tone = this.sessionTone(s);
    if (tone === 'one_to_one') {
      return this.setLabel('typeOneToOneLabel');
    }
    if (tone === 'program') {
      return this.setLabel('typeProgramLabel');
    }
    return this.setLabel('typeClassLabel');
  }

  classDialogHeaderLabel(): string {
    return this.sessionTone(this.classSession) === 'program'
      ? this.setLabel('programChecklistHeaderLabel')
      : this.setLabel('classChecklistHeaderLabel');
  }

  customerName(s: AgendaSession): string {
    if (s.type === 'class') {
      const n = (s.bookings || []).length;
      if (!n) {
        return '—';
      }
      return `${n} ${this.setLabel(n === 1 ? 'memberSingularLabel' : 'memberPluralLabel')}`;
    }
    const b = this.oneToOneBooking(s);
    if (!b) {
      return this.setLabel('noBookingsLabel');
    }
    return (b.customerName || '').trim() || this.setLabel('memberFallbackLabel');
  }

  /** Attendance view: booked people (any status), not live confirmed capacity. */
  seatsDisplay(s: AgendaSession): string {
    const taken = Array.isArray(s.bookings) ? s.bookings.length : 0;
    return `${taken}/${s.maxMembers ?? 0}`;
  }

  timeRange(s: AgendaSession): string {
    return `${this.hhmm(s.startMinutes)}–${this.hhmm(
      s.startMinutes + s.durationMinutes,
    )}`;
  }

  statusLabel(status: string): string {
    switch (status) {
      case 'Present':
        return this.setLabel('statusCompletedLabel');
      case 'Absent':
        return this.setLabel('statusNoShowLabel');
      default:
        return this.setLabel('statusConfirmedLabel');
    }
  }

  oneToOneBooking(s: AgendaSession): AgendaBooking | null {
    if (s.type !== 'one_to_one') {
      return null;
    }
    return (s.bookings && s.bookings[0]) || null;
  }

  attendanceDisabledReason(
  b: AgendaBooking | null | undefined,
): string {
  if (!b) {
    return '';
  }

  const session = this.rows.find((s) =>
    s.bookings.some((booking) => booking._id === b._id),
  );

  if (!session) {
    return 'Session date is unavailable';
  }

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Karachi',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());

  const today = [
    parts.find((p) => p.type === 'year')!.value,
    parts.find((p) => p.type === 'month')!.value,
    parts.find((p) => p.type === 'day')!.value,
  ].join('-');

  // Do not display disabled messages for past or future sessions.

  return '';
}

canMark(b: AgendaBooking | null | undefined): boolean {
  return (
    !!b &&
    b.status === 'confirmed' &&
    !this.markingId &&
    !this.classMarkBusy &&
    !this.attendanceDisabledReason(b)
  );
}

  openClassDialog(s: AgendaSession): void {
    this.classSession = s;
    this.classMarks = {};
    this.classMarkBusy = false;
    this.classDialogVisible = true;
  }

  closeClassDialog(): void {
    this.classDialogVisible = false;
    this.classSession = null;
    this.classMarks = {};
    this.classMarkBusy = false;
  }

  classMarkOf(b: AgendaBooking): 'Present' | 'Absent' | null {
    return this.classMarks[b._id] || null;
  }

  setClassMark(
    b: AgendaBooking,
    status: 'Present' | 'Absent',
    checked: boolean,
  ): void {
    if (!this.canMark(b)) {
      return;
    }
    const next = { ...this.classMarks };
    if (!checked) {
      if (next[b._id] === status) {
        delete next[b._id];
      }
      this.classMarks = next;
      return;
    }
    if (
      status === 'Present' &&
      this.assessmentRequired &&
      !this.hasAssessment(b.customerId) &&
      this.classSession
    ) {
      this.openAssessment(this.classSession, b, false);
      return;
    }
    next[b._id] = status;
    this.classMarks = next;
  }

  get classPendingCount(): number {
    return Object.keys(this.classMarks).length;
  }

  markClassAttendance(): void {
    if (!this.branchId || !this.classSession || this.classMarkBusy) {
      return;
    }
    const pending = Object.entries(this.classMarks);
    if (!pending.length) {
      return;
    }
for (const [bookingId] of pending) {
  const booking = this.classSession.bookings.find(
    (b) => b._id === bookingId,
  );

  if (!this.canMark(booking)) {
    return;
  }
}
    this.classMarkBusy = true;
    const runNext = (i: number) => {
      if (i >= pending.length) {
        this.classMarkBusy = false;
        this.classMarks = {};
        this.loadAgenda(() => {
          if (this.classDialogVisible && this.classSession) {
            const refreshed = this.rows.find(
              (r) => r._id === this.classSession!._id,
            );
            if (refreshed) {
              this.classSession = refreshed;
            }
          }
        });
        return;
      }
      const [bookingId, status] = pending[i];
      this.appServiceManager
        .patch(
          `branches/${this.branchId}/trainer-bookings/${bookingId}/attendance`,
          { status },
        )
        .subscribe(
          () => runNext(i + 1),
          (err) => {
            this.classMarkBusy = false;
            this.toastError(err, this.setLabel('markFailedLabel'));
            this.loadAgenda(() => {
              if (this.classDialogVisible && this.classSession) {
                const refreshed = this.rows.find(
                  (r) => r._id === this.classSession!._id,
                );
                if (refreshed) {
                  this.classSession = refreshed;
                }
              }
            });
          },
        );
    };
    runNext(0);
  }

  markPresent(s: AgendaSession, b: AgendaBooking): void {
    if (this.assessmentRequired && !this.hasAssessment(b.customerId)) {
      this.openAssessment(s, b, false);
      return;
    }
    this.mark(b, 'Present');
  }

  markNoShow(b: AgendaBooking): void {
    this.mark(b, 'Absent');
  }

  private mark(b: AgendaBooking, status: 'Present' | 'Absent'): void {
    if (!this.canMark(b) || !this.branchId) {
      return;
    }
    this.markingId = b._id;
    this.appServiceManager
      .patch(
        `branches/${this.branchId}/trainer-bookings/${b._id}/attendance`,
        { status },
      )
      .subscribe(
        () => {
          this.markingId = null;
          this.loadAgenda(() => {
            if (this.classDialogVisible && this.classSession) {
              const refreshed = this.rows.find(
                (r) => r._id === this.classSession!._id,
              );
              if (refreshed) {
                this.classSession = refreshed;
              }
            }
          });
        },
        (err) => {
          this.markingId = null;
          this.toastError(err, this.setLabel('markFailedLabel'));
        },
      );
  }

  private loadTrainers(): void {
    if (!this.branchId) {
      return;
    }
    this.appServiceManager
      .get(`branches/${this.branchId}/users/actions/trainers`)
      .subscribe(
        (res: any[]) => {
          const rows = (Array.isArray(res) ? res : []).filter(
            (t) => t && t.isDelete === false,
          );
          rows.sort((a, b) =>
            String(a.name || '').localeCompare(String(b.name || '')),
          );
          this.trainerOptions = rows.map((t) => ({
            label: t.name,
            value: String(t._id),
          }));
        },
        () => {
          this.trainerOptions = [];
        },
      );
  }

  private loadAgenda(done?: () => void): void {
    if (!this.branchId || !this.selectedLocalDate) {
      this.rows = [];
      done?.();
      return;
    }
    this.loading = true;
    let url = `branches/${this.branchId}/trainer-sessions/day-agenda?localDate=${this.selectedLocalDate}`;
    // Trainer: always own slots. Admin/staff: optional trainerId (omit = all).
    const trainerId = this.isTrainerRole
      ? this.userId
      : this.selectedTrainerId;
    if (trainerId) {
      url += `&trainerId=${encodeURIComponent(String(trainerId))}`;
    }
    this.appServiceManager.get(url).subscribe(
      (res: AgendaSession[]) => {
        this.loading = false;
        this.rows = Array.isArray(res) ? res : [];
        this.loadAssessments(done);
      },
      (err) => {
        this.loading = false;
        this.rows = [];
        this.toastError(err, this.setLabel('loadFailedLabel'));
        done?.();
      },
    );
  }

  private toLocalDateString(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private shiftLocalDate(iso: string, deltaDays: number): string {
    const [y, m, d] = iso.split('-').map((n) => Number(n));
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + deltaDays);
    return this.toLocalDateString(dt);
  }

  private hhmm(totalMinutes: number): string {
    const m = Math.max(0, Math.floor(totalMinutes));
    const h24 = Math.floor(m / 60) % 24;
    const min = m % 60;
    const ampm = h24 >= 12 ? 'PM' : 'AM';
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return `${h12}:${String(min).padStart(2, '0')} ${ampm}`;
  }

  hasAssessment(customerId: string): boolean {
    return this.assessedIds.has(String(customerId));
  }

  openAssessmentView(s: AgendaSession, b: AgendaBooking): void {
    this.openAssessment(s, b, true);
  }

  closeAssessment(): void {
    if (this.assessmentSaving) {
      return;
    }
    this.assessmentVisible = false;
    this.assessmentBooking = null;
    this.assessmentSession = null;
  }

  downloadAssessment(): void {
    document.body.classList.add('sa-print-assessment');
    const clear = () => document.body.classList.remove('sa-print-assessment');
    window.addEventListener('afterprint', clear, { once: true });
    window.print();
  }

  saveAssessment(): void {
    const booking = this.assessmentBooking;
    const session = this.assessmentSession;
    if (!booking || !session || !this.branchId || this.assessmentReadOnly) {
      return;
    }
    const missing = this.missingAssessmentFields();
    if (missing.length) {
      this.messageService.add({
        severity: 'error',
        summary: this.setLabel('assessmentFillAllLabel'),
        detail: missing.join(', '),
        life: 10000,
      });
      return;
    }
    const yearMonth = String(session.localDate || '').slice(0, 7);
    this.assessmentSaving = true;
    this.appServiceManager
      .put(`branches/${this.branchId}/trainer-assessments`, {
        customerId: booking.customerId,
        trainerId: session.trainerId,
        sessionId: session._id,
        yearMonth,
        clientName: this.assessmentForm.clientName,
        trainerName: this.assessmentForm.trainerName,
        signedOn:
          this.assessmentForm.answers.progress.clientDate ||
          this.assessmentForm.signedOn,
        answers: this.assessmentForm.answers,
      })
      .subscribe(
        () => {
          this.assessmentSaving = false;
          this.assessedIds.add(String(booking.customerId));
          this.assessmentVisible = false;
          this.mark(booking, 'Present');
        },
        (err) => {
          this.assessmentSaving = false;
          this.toastError(err, this.setLabel('assessmentSaveFailedLabel'));
        },
      );
  }

  toggleGoal(value: string, checked: boolean): void {
    const goals = this.assessmentForm.answers.goals.selected;
    const next = goals.filter((item) => item !== value);
    if (checked) {
      next.push(value);
    }
    this.assessmentForm.answers.goals.selected = next;
  }

  setChoice(bag: object, key: string, value: string, checked: boolean): void {
    (bag as Record<string, string>)[key] = checked ? value : '';
  }

  goalChecked(value: string): boolean {
    return this.assessmentForm.answers.goals.selected.includes(value);
  }

  toggleTrainingType(value: string, checked: boolean): void {
    const list = this.assessmentForm.answers.training.typeList || [];
    const next = list.filter((item) => item !== value);
    if (checked) {
      next.push(value);
    }
    this.assessmentForm.answers.training.typeList = next;
    this.assessmentForm.answers.training.types = next.join(', ');
  }

  trainingTypeChecked(value: string): boolean {
    return (this.assessmentForm.answers.training.typeList || []).includes(value);
  }

  /** Capital Arena is the only branch that uses this monthly form. */
  get assessmentRequired(): boolean {
    return this.branchId === '67f102774cf88446d8ce9929';
  }

  private missingAssessmentFields(): string[] {
    const answers = this.assessmentForm.answers;
    const missing: string[] = [];
    const need = (value: unknown, label: string) => {
      if (!String(value ?? '').trim()) {
        missing.push(label);
      }
    };
    const detailIfYes = (choice: string, detail: string, label: string) => {
      if (choice === 'Yes') {
        need(detail, `${label} ${this.setLabel('assessmentDetailLabel')}`);
      }
    };
    need(answers.client.fullName, this.setLabel('assessmentFullNameLabel'));
    need(answers.client.dateOfBirth, this.setLabel('assessmentDobLabel'));
    need(answers.client.age, this.setLabel('assessmentAgeLabel'));
    need(answers.client.gender, this.setLabel('assessmentGenderLabel'));
    need(answers.client.phone, this.setLabel('assessmentPhoneLabel'));
    need(answers.client.email, this.setLabel('assessmentEmailLabel'));
    need(answers.client.address, this.setLabel('assessmentAddressLabel'));
    need(answers.client.occupation, this.setLabel('assessmentOccupationLabel'));
    need(answers.client.emergencyName, this.setLabel('assessmentEmergencyNameLabel'));
    need(answers.client.emergencyPhone, this.setLabel('assessmentEmergencyPhoneLabel'));
    if (!answers.goals.selected?.length) {
      missing.push(this.setLabel('assessmentGoalsLabel'));
    }
    need(answers.goals.other, this.setLabel('assessmentOtherLabel'));
    need(answers.goals.targetDate, this.setLabel('assessmentTargetDateLabel'));
    need(answers.goals.motivation, this.setLabel('assessmentMotivationLabel'));
    need(answers.lifestyle.daysPerWeek, this.setLabel('assessmentDaysPerWeekLabel'));
    need(answers.lifestyle.sleepBand, this.setLabel('assessmentSleepLabel'));
    need(answers.lifestyle.stressLevel, this.setLabel('assessmentStressLabel'));
    need(answers.lifestyle.workType, this.setLabel('assessmentWorkTypeLabel'));
    need(answers.lifestyle.specificDiet, this.setLabel('assessmentSpecificDietLabel'));
    detailIfYes(
      answers.lifestyle.specificDiet,
      answers.lifestyle.dietDetail,
      this.setLabel('assessmentSpecificDietLabel'),
    );
    need(answers.lifestyle.smokes, this.setLabel('assessmentSmokesLabel'));
    need(answers.lifestyle.alcohol, this.setLabel('assessmentAlcoholLabel'));
    need(answers.lifestyle.waterIntake, this.setLabel('assessmentWaterLabel'));
    need(answers.medical.conditions, this.setLabel('assessmentConditionsLabel'));
    detailIfYes(
      answers.medical.conditions,
      answers.medical.conditionsDetail,
      this.setLabel('assessmentConditionsLabel'),
    );
    need(answers.medical.injuries, this.setLabel('assessmentInjuriesLabel'));
    detailIfYes(
      answers.medical.injuries,
      answers.medical.injuriesDetail,
      this.setLabel('assessmentInjuriesLabel'),
    );
    need(answers.medical.medication, this.setLabel('assessmentMedicationLabel'));
    detailIfYes(
      answers.medical.medication,
      answers.medical.medicationDetail,
      this.setLabel('assessmentMedicationLabel'),
    );
    need(answers.medical.allergies, this.setLabel('assessmentAllergiesLabel'));
    detailIfYes(
      answers.medical.allergies,
      answers.medical.allergiesDetail,
      this.setLabel('assessmentAllergiesLabel'),
    );
    need(answers.training.trainedBefore, this.setLabel('assessmentTrainedBeforeLabel'));
    if (answers.training.trainedBefore === 'Yes') {
      need(answers.training.howLong, this.setLabel('assessmentHowLongLabel'));
    }
    if (!(answers.training.typeList || []).length) {
      missing.push(this.setLabel('assessmentTrainingTypesLabel'));
    }
    need(answers.training.level, this.setLabel('assessmentLevelLabel'));
    need(answers.training.challenges, this.setLabel('assessmentChallengesLabel'));
    const measures: { key: keyof AssessmentAnswers['measurements']; label: string }[] = [
      { key: 'weight', label: 'Weight (kg)' },
      { key: 'height', label: 'Height (cm / ft)' },
      { key: 'waist', label: 'Waist (cm)' },
      { key: 'hips', label: 'Hips (cm)' },
      { key: 'chest', label: 'Chest (cm)' },
      { key: 'arms', label: 'Arms (cm)' },
      { key: 'thighs', label: 'Thighs (cm)' },
    ];
    for (const row of measures) {
      need(answers.measurements[row.key]?.current, `${row.label} ${this.setLabel('assessmentCurrentLabel')}`);
      need(answers.measurements[row.key]?.goal, `${row.label} ${this.setLabel('assessmentGoalLabel')}`);
    }
    need(answers.posture.issues, this.setLabel('assessmentPostureIssuesLabel'));
    detailIfYes(
      answers.posture.issues,
      answers.posture.issuesDetail,
      this.setLabel('assessmentPostureIssuesLabel'),
    );
    need(answers.posture.limitations, this.setLabel('assessmentLimitationsLabel'));
    detailIfYes(
      answers.posture.limitations,
      answers.posture.limitationsDetail,
      this.setLabel('assessmentLimitationsLabel'),
    );
    if (!(answers.posture.flags || []).length) {
      missing.push(this.setLabel('assessmentPostureLabel'));
    }
    need(answers.tests.pushUps, this.setLabel('assessmentPushUpsLabel'));
    need(answers.tests.squat, this.setLabel('assessmentSquatLabel'));
    need(answers.tests.plank, this.setLabel('assessmentPlankLabel'));
    need(answers.tests.runWalk, this.setLabel('assessmentRunWalkLabel'));
    need(answers.tests.flexibility, this.setLabel('assessmentFlexibilityLabel'));
    need(answers.tests.grip, this.setLabel('assessmentGripLabel'));
    need(answers.tests.other, `${this.setLabel('assessmentTestsLabel')} ${this.setLabel('assessmentOtherLabel')}`);
    need(answers.nutrition.mealsPerDay, this.setLabel('assessmentMealsLabel'));
    need(answers.nutrition.tracksMacros, this.setLabel('assessmentTracksMacrosLabel'));
    need(answers.nutrition.dietType, this.setLabel('assessmentDietTypeLabel'));
    need(answers.nutrition.intolerances, this.setLabel('assessmentIntolerancesLabel'));
    detailIfYes(
      answers.nutrition.intolerances,
      answers.nutrition.intolerancesDetail,
      this.setLabel('assessmentIntolerancesLabel'),
    );
    need(answers.nutrition.hydration, this.setLabel('assessmentHydrationLabel'));
    need(answers.extra.preferences, this.setLabel('assessmentPreferencesLabel'));
    detailIfYes(
      answers.extra.preferences,
      answers.extra.preferencesDetail,
      this.setLabel('assessmentPreferencesLabel'),
    );
    need(answers.extra.environment, this.setLabel('assessmentEnvironmentLabel'));
    need(answers.program.shortTerm, this.setLabel('assessmentShortTermLabel'));
    need(answers.program.midTerm, this.setLabel('assessmentMidTermLabel'));
    need(answers.program.longTerm, this.setLabel('assessmentLongTermLabel'));
    need(answers.program.frequencyDays, this.setLabel('assessmentFrequencyLabel'));
    if (!(answers.program.focus || []).length) {
      missing.push('Session focus');
    }
    need(answers.program.preferredTime, this.setLabel('assessmentPreferredTimeLabel'));
    need(answers.progress.reviewDate, this.setLabel('assessmentReviewDateLabel'));
    need(answers.progress.nextCheckIn, this.setLabel('assessmentNextCheckInLabel'));
    need(answers.progress.notes, this.setLabel('assessmentNotesLabel'));
    need(this.assessmentForm.clientName, this.setLabel('assessmentClientSignLabel'));
    need(answers.progress.clientDate, `${this.setLabel('assessmentClientSignLabel')} ${this.setLabel('assessmentSignedOnLabel')}`);
    need(this.assessmentForm.trainerName, this.setLabel('assessmentTrainerSignLabel'));
    need(answers.progress.trainerDate, `${this.setLabel('assessmentTrainerSignLabel')} ${this.setLabel('assessmentSignedOnLabel')}`);
    return missing;
  }

  get assessmentGymName(): string {
    try {
      const company = JSON.parse(localStorage.getItem('currentCompany') || 'null');
      return company?.name || 'Fitness Club';
    } catch {
      return 'Fitness Club';
    }
  }

  toggleList(
    section: 'posture' | 'program',
    field: 'flags' | 'focus',
    value: string,
    checked: boolean,
  ): void {
    const bag = this.assessmentForm.answers[section] as unknown as {
      flags: string[];
      focus: string[];
    };
    const current = bag[field] || [];
    const next = current.filter((item) => item !== value);
    if (checked) {
      next.push(value);
    }
    bag[field] = next;
  }

  listChecked(
    section: 'posture' | 'program',
    field: 'flags' | 'focus',
    value: string,
  ): boolean {
    const bag = this.assessmentForm.answers[section] as unknown as {
      flags?: string[];
      focus?: string[];
    };
    return (bag[field] || []).includes(value);
  }

  private openAssessment(
    s: AgendaSession,
    b: AgendaBooking,
    readOnly: boolean,
  ): void {
    this.assessmentSession = s;
    this.assessmentBooking = b;
    this.assessmentReadOnly = readOnly;
    this.assessmentForm = this.emptyAssessment();
    this.assessmentForm.clientName = b.customerName || '';
    this.assessmentForm.answers.client.fullName = b.customerName || '';
    this.assessmentForm.answers.client.phone = b.customerPhone || '';
    this.assessmentForm.trainerName = s.trainerName || '';
    this.assessmentForm.signedOn = s.localDate || '';
    this.assessmentForm.answers.progress.clientDate = s.localDate || '';
    this.assessmentForm.answers.progress.trainerDate = s.localDate || '';
    this.assessmentVisible = true;
    if (!readOnly) {
      return;
    }
    const yearMonth = String(s.localDate || '').slice(0, 7);
    this.appServiceManager
      .get(
        `branches/${this.branchId}/trainer-assessments?customerId=${b.customerId}&yearMonth=${yearMonth}`,
      )
      .subscribe((row: { clientName?: string; trainerName?: string; signedOn?: string; answers?: AssessmentAnswers } | null) => {
        if (!row) {
          return;
        }
        this.assessmentForm.clientName = row.clientName || this.assessmentForm.clientName;
        this.assessmentForm.trainerName = row.trainerName || this.assessmentForm.trainerName;
        this.assessmentForm.signedOn = row.signedOn || this.assessmentForm.signedOn;
        this.assessmentForm.answers = this.mergeAnswers(row.answers);
      });
  }

  private loadAssessments(done?: () => void): void {
    const ids = new Set<string>();
    for (const row of this.rows) {
      for (const booking of row.bookings || []) {
        if (booking.customerId) {
          ids.add(String(booking.customerId));
        }
      }
    }
    const yearMonth = String(this.selectedLocalDate || '').slice(0, 7);
    if (!this.assessmentRequired || !this.branchId || !ids.size || yearMonth.length < 7) {
      this.assessedIds = new Set();
      done?.();
      return;
    }
    this.appServiceManager
      .get(
        `branches/${this.branchId}/trainer-assessments?yearMonth=${yearMonth}&customerIds=${encodeURIComponent([...ids].join(','))}`,
      )
      .subscribe(
        (rows: { customerId?: string }[]) => {
          this.assessedIds = new Set(
            (Array.isArray(rows) ? rows : []).map((row) => String(row.customerId)),
          );
          done?.();
        },
        () => {
          this.assessedIds = new Set();
          done?.();
        },
      );
  }

  private emptyAssessment(): AssessmentForm {
    const measure = () => ({ current: '', goal: '' });
    return {
      clientName: '',
      trainerName: '',
      signedOn: '',
      answers: {
        client: {
          fullName: '',
          dateOfBirth: '',
          age: '',
          gender: '',
          phone: '',
          email: '',
          address: '',
          occupation: '',
          emergencyName: '',
          emergencyPhone: '',
        },
        goals: { selected: [], other: '', targetDate: '', motivation: '' },
        lifestyle: {
          daysPerWeek: '',
          sleepBand: '',
          stressLevel: '',
          workType: '',
          specificDiet: '',
          dietDetail: '',
          smokes: '',
          alcohol: '',
          waterIntake: '',
        },
        medical: {
          conditions: '',
          conditionsDetail: '',
          injuries: '',
          injuriesDetail: '',
          medication: '',
          medicationDetail: '',
          allergies: '',
          allergiesDetail: '',
        },
        training: {
          trainedBefore: '',
          howLong: '',
          types: '',
          typeList: [],
          level: '',
          challenges: '',
        },
        measurements: {
          weight: measure(),
          height: measure(),
          waist: measure(),
          hips: measure(),
          chest: measure(),
          arms: measure(),
          thighs: measure(),
        },
        posture: { issues: '', issuesDetail: '', limitations: '', limitationsDetail: '', flags: [] },
        tests: {
          pushUps: '',
          squat: '',
          plank: '',
          runWalk: '',
          flexibility: '',
          grip: '',
          other: '',
        },
        nutrition: {
          mealsPerDay: '',
          tracksMacros: '',
          dietType: '',
          intolerances: '',
          intolerancesDetail: '',
          hydration: '',
        },
        extra: {
          preferences: '',
          preferencesDetail: '',
          environment: '',
          comments: '',
        },
        program: {
          shortTerm: '',
          midTerm: '',
          longTerm: '',
          frequencyDays: '',
          focus: [],
          preferredTime: '',
        },
        progress: {
          reviewDate: '',
          nextCheckIn: '',
          notes: '',
          clientDate: '',
          trainerDate: '',
        },
      },
    };
  }

  private mergeAnswers(raw: AssessmentAnswers | undefined): AssessmentAnswers {
    const base = this.emptyAssessment().answers;
    if (!raw) {
      return base;
    }
    return {
      ...base,
      ...raw,
      client: { ...base.client, ...(raw.client || {}) },
      goals: { ...base.goals, ...(raw.goals || {}), selected: raw.goals?.selected || [] },
      lifestyle: { ...base.lifestyle, ...(raw.lifestyle || {}) },
      medical: { ...base.medical, ...(raw.medical || {}) },
      training: {
        ...base.training,
        ...(raw.training || {}),
        typeList: raw.training?.typeList || [],
      },
      measurements: { ...base.measurements, ...(raw.measurements || {}) },
      posture: { ...base.posture, ...(raw.posture || {}), flags: raw.posture?.flags || [] },
      tests: { ...base.tests, ...(raw.tests || {}) },
      nutrition: { ...base.nutrition, ...(raw.nutrition || {}) },
      extra: { ...base.extra, ...(raw.extra || {}) },
      program: { ...base.program, ...(raw.program || {}), focus: raw.program?.focus || [] },
      progress: { ...base.progress, ...(raw.progress || {}) },
    };
  }

  private toastError(err: any, fallback: string): void {
    const detail =
      err?.error?.message ||
      err?.message ||
      (typeof err?.error === 'string' ? err.error : null) ||
      fallback;
    this.messageService.add({
      severity: 'error',
      summary: fallback,
      detail: Array.isArray(detail) ? detail.join(', ') : String(detail),
    });
  }
}
